import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useNavigate } from 'react-router';

import {
  getArtSettingsCommon,
  saveArtSettingsCommon,
} from '../../../../../packages/common/api';
import CheckboxIcon from '../../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from '../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteIcon from '../../../../../packages/common/shared-ui/src/icons/DeleteIcon';
import { useTranslation } from '../../../../../packages/common/utils/getLocalString';

// Original class: chip
const StyledChip = styled(Chip)(theme => ({
  '&.MuiChip-root': {
    height: '30px',
    margin: '3px 4px',
    fontSize: '12px',
    display: 'flex',
    border: '1px solid #E4E4EF',
    borderRadius: '14px',
    '& .MuiChip-label': {
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      marginRight: '5px',
      position: 'static',
      height: '10px !important',
    },
  },
}));
// Original class: fieldAutocomplete
const StyledFieldAutocomplete = styled(Autocomplete, {
  shouldForwardProp: prop => !['multipleStyle'].includes(prop),
})((theme, multipleStyle) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    background: '#fff',
    //padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    minHeight: '36px',
    padding: '0 65px 0 0',
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
      marginLeft: '16px',
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
    '&:hover': {
      '&.MuiInputBase-root': {
        '&.MuiOutlinedInput-root': {
          backgroundColor: '#fff',
        },
      },
    },
  },
  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          padding: '8px 10px',
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '& .MuiButtonBase-root.MuiCheckbox-root': {
            padding: '2px',
            marginRight: '10px',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));

// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    position: 'static',
    width: '200px',
    color: '#41424E',
    fontSize: '14px',
    lineHeight: '20px',
    transform: 'none',
    '& .MuiFormLabel-asterisk': {
      color: '#E03737',
    },
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
  },
}));

const ArtCommon = forwardRef((props, ref) => {
  const getLocalString = useTranslation();
  const navigate = useNavigate();
  const { formChanged, ...otherProps } = props;
  const [fields, setFields] = useState([]);
  const [initialFields, setInitialFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );
  useImperativeHandle(ref, () => {
    return {
      async save() {
        setInitialFields([]);
        const result = fields
          .filter(field => field.changed)
          .reduce((acc, field) => {
            if (Array.isArray(field.value)) {
              acc[field.fieldId] = field.value.map(item =>
                Number(item.valueId),
              );
            } else {
              acc[field.fieldId] =
                field.fieldDefinition.fieldFormat === 'bool'
                  ? field.value
                  : Number(field.value.valueId);
            }
            return acc;
          }, {});
        try {
          setLoading(true);
          const { data: res } = await saveArtSettingsCommon(result);
          if (res) {
            const formattedFields = res.map(item => {
              let value;
              if (item.fieldDefinition.fieldFormat === 'bool') {
                value = item.valueId === 'true';
              } else if (item.fieldDefinition.isMultiple) {
                value = item.fieldDefinition.values.filter(
                  item => item.isChecked,
                );
              } else {
                value = item.fieldDefinition.values.find(
                  item => item.isChecked,
                );
              }
              return {
                ...item,
                value: value,
              };
            });
            setFields(formattedFields);
            setInitialFields(formattedFields);
            formChanged(false);
          }
          setLoading(false);
        } catch (error) {
          setLoading(false);
          handleError(error.response);
        }
      },
      cancel() {
        setFields(initialFields);
        formChanged(false);
      },
    };
  });

  useEffect(() => {
    let ignore = false;
    async function getArtCommon() {
      try {
        const { data: settings } = await getArtSettingsCommon();
        if (!ignore) {
          const formattedFields = settings.map(item => {
            let value;

            if (item.fieldDefinition.fieldFormat === 'bool') {
              value = item.valueId === 'true';
            } else if (item.fieldDefinition.isMultiple) {
              value = item.fieldDefinition.values.filter(
                item => item.isChecked,
              );
            } else {
              value = item.fieldDefinition.values.find(item => item.isChecked);
            }

            return {
              ...item,
              value: value,
            };
          });
          setLoading(false);
          setFields(formattedFields);
          setInitialFields(formattedFields);
        }
      } catch (error) {
        if (!ignore) {
          setLoading(false);
          handleError(error.response);
          console.error('Error fetching settings:', error);
        }
      }
    }
    getArtCommon();
    return () => {
      ignore = true;
    };
  }, [handleError]);

  const handleField = useCallback(
    (data, field, deleted = false) => {
      let updatedObject;
      const pos = fields.map(e => e.fieldId).indexOf(field.fieldId);
      updatedObject = {
        ...fields[pos],
        valueId: Array.isArray(data)
          ? data.map(item => (item.valueId ? item.valueId : item))
          : data.valueId,
        valueName: Array.isArray(data)
          ? data.map(item => (item.valueName ? item.valueName : item))
          : data.valueName,
        value: data,
        changed: true,
      };
      const newFields = [
        ...fields.slice(0, pos),
        updatedObject,
        ...fields.slice(pos + 1),
      ];
      setFields(newFields);
      formChanged(true);
    },
    [fields, formChanged],
  );
  return (
    <Box
      sx={{
        marginTop: '16px',
        padding: '24px 20px',
        borderRadius: '16px',
        backgroundColor: fields.length > 0 ? '#fff' : 'tranparent',
        position: 'relative',
      }}
    >
      {loading && (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            zIndex: 99,
            top: 0,
            position: 'absolute',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      )}
      {fields.length > 0 && (
        <Grid container columnSpacing={10}>
          {fields.map((field, index) => (
            <Grid
              size={6}
              key={index}
              sx={{
                marginTop: index > 1 ? '16px' : 0,
              }}
            >
              <FormControl
                required={field.fieldDefinition.isRequired}
                key={field.fieldId}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                }}
              >
                <StyledSelectLabel
                  sx={{
                    width: '100% !important',
                    maxWidth: 'none',
                    whiteSpace: 'normal',
                  }}
                  style={{
                    width: '100%',
                    maxWidth: 'none',
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'unset',
                    display: 'block',
                    position: 'static',
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      color: '#41424E',
                      width: '100%',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {field.fieldName}
                  </Typography>
                </StyledSelectLabel>
                {field.fieldDefinition.fieldFormat === 'bool' ? (
                  <Box display='flex' alignItems='center'>
                    <Checkbox
                      disableRipple
                      checked={field.value === true}
                      onChange={event =>
                        handleField(event.target.checked, field)
                      }
                      icon={
                        <CheckboxIcon
                          width='12'
                          height='12'
                          viewBox='0 0 12 12'
                        />
                      }
                      checkedIcon={
                        <CheckboxIconChecked
                          width='12'
                          height='12'
                          viewBox='0 0 12 12'
                        />
                      }
                    />
                  </Box>
                ) : field.fieldDefinition.isMultiple ? (
                  <StyledFieldAutocomplete
                    multipleStyle={true}
                    forcePopupIcon={false}
                    multiple
                    disableClearable
                    onChange={(event, newValue) => handleField(newValue, field)}
                    options={field.fieldDefinition.values}
                    disableCloseOnSelect
                    disablePortal
                    value={field.value ? field.value : []}
                    isOptionEqualToValue={(option, value) =>
                      option.valueId === value.valueId
                    }
                    getOptionLabel={option => option.valueName || ''}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.valueId}>
                        <Checkbox
                          icon=<CheckboxIcon
                            width='12'
                            height='12'
                            viewBox='0 0 12 12'
                          />
                          checkedIcon=<CheckboxIconChecked
                            width='12'
                            height='12'
                            viewBox='0 0 12 12'
                          />
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.valueName}
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        return (
                          <StyledChip
                            {...getTagProps({ index })}
                            key={index}
                            deleteIcon={
                              <DeleteIcon
                                width={10}
                                height={10}
                                viewBox='0 0 10 10'
                              />
                            }
                            variant='outlined'
                            label={option.valueName}
                          />
                        );
                      })
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={getLocalString('labels', 'input')}
                      />
                    )}
                  />
                ) : (
                  <StyledFieldAutocomplete
                    noOptionsText={getLocalString('labels', 'notFound')}
                    onChange={(event, newValue) => handleField(newValue, field)}
                    disablePortal
                    options={field.fieldDefinition.values}
                    renderOption={(props, option) => {
                      return (
                        <MenuItem
                          {...props}
                          key={option.valueId}
                          value={option.valueId}
                          selected={option.valueId === field.valueId}
                        >
                          {option.valueName}
                        </MenuItem>
                      );
                    }}
                    value={field.value ? field.value : null}
                    isOptionEqualToValue={(option, value) =>
                      option.valueId === value.valueId
                    }
                    getOptionLabel={option => option.valueName || ''}
                    slots={{
                      popper: props => (
                        <Popper
                          {...props}
                          sx={{ maxWidth: '100%!important' }}
                          placement='bottom-start'
                        ></Popper>
                      ),
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={field.error}
                        placeholder={getLocalString('labels', 'select')}
                      />
                    )}
                    popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
                  />
                )}
              </FormControl>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
});

export default ArtCommon;
